import React from "react";
import PersonAvatar from "../home/schedule_components/PersonAvatar";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import { objectToCSV, downloadCSV } from "../../../utilities/csvExport";
import QuizReviewModal from "../create_training_content/QuizReviewModal";

function QuizRecordTable({
  record,
  onBack,
  homeCompany,
  language = "english",
  employeeAvatarColorByUserId,
}) {
  const [selectedAttemptUuid, setSelectedAttemptUuid] = React.useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = React.useState(false);

  const handleViewAttempt = (attemptUuid) => {
    setSelectedAttemptUuid(attemptUuid);
    setIsReviewModalOpen(true);
  };

  const handleCloseReviewModal = () => {
    setIsReviewModalOpen(false);
    setSelectedAttemptUuid(null);
  };
  const formatDateReadable = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const hasSubmissionData = record.submissions && record.submissions.length > 0;
  const hasIncompleteData = record.incomplete_assignments && record.incomplete_assignments.length > 0;
  const hasEmployeeData = hasSubmissionData || hasIncompleteData;

  // Group submissions by employee
  const groupedSubmissions = React.useMemo(() => {
    if (!record.submissions) return [];

    const groups = {};
    record.submissions.forEach(submission => {
      const key = submission.employee_id;
      if (!groups[key]) {
        groups[key] = {
          employee_id: submission.employee_id,
          employee_name: submission.employee_name,
          attempts: []
        };
      }
      groups[key].attempts.push(submission);
    });

    // Sort attempts within each group by attempt_number
    // Legacy completions (null attempt_number) appear first
    Object.values(groups).forEach(group => {
      group.attempts.sort((a, b) => {
        // If both are null, maintain order
        if (a.attempt_number === null && b.attempt_number === null) return 0;
        // Null values (legacy) come first
        if (a.attempt_number === null) return -1;
        if (b.attempt_number === null) return 1;
        // Otherwise sort by attempt number
        return a.attempt_number - b.attempt_number;
      });
    });

    return Object.values(groups);
  }, [record.submissions]);

  const exportQuizCSV = () => {
    const rows = [];
    groupedSubmissions.forEach((group) => {
      group.attempts.forEach((attempt) => {
        rows.push({
          "Employee": group.employee_name,
          "Score": attempt.is_legacy
            ? "Passed (Legacy)"
            : attempt.score !== null && attempt.score !== undefined
            ? `${attempt.score}%`
            : "",
          "Result": attempt.passed ? "Passed" : "Failed",
          "Attempt #": attempt.is_legacy
            ? "N/A"
            : attempt.attempt_number ?? "",
          "Date Completed": attempt.date_completed
            ? formatDateReadable(attempt.date_completed)
            : "",
        });
      });
    });
    (record.incomplete_assignments || [])
      .filter(
        (a) =>
          !groupedSubmissions.some((g) => g.employee_id === a.employee_id)
      )
      .forEach((assignment) => {
        rows.push({
          "Employee": assignment.employee_name,
          "Score": "Not yet attempted",
          "Result": "Pending",
          "Attempt #": "0",
          "Date Completed": "",
        });
      });

    if (rows.length === 0) return;
    const csv = objectToCSV(rows);
    downloadCSV(csv, `quiz_${record.name.replace(/\s+/g, "_")}.csv`);
  };

  // Filter incomplete assignments to exclude employees who already have submissions
  const filteredIncompleteAssignments = React.useMemo(() => {
    if (!record.incomplete_assignments) return [];

    const employeeIdsWithSubmissions = new Set(
      groupedSubmissions.map(group => group.employee_id)
    );

    return record.incomplete_assignments.filter(
      assignment => !employeeIdsWithSubmissions.has(assignment.employee_id)
    );
  }, [record.incomplete_assignments, groupedSubmissions]);

  return (
    <div className="px-6 py-8">
      {/* Header with back button */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-violet-600 hover:text-violet-700 text-sm font-medium mb-4 transition-colors"
        >
          <ChevronLeftIcon className="-ml-2" style={{ fontSize: "24px" }} />
          Back to Records
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-1">
              {record.name}
            </h2>
            <p className="text-sm text-slate-600">Quiz completion records</p>
          </div>
          <button
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
            onClick={exportQuizCSV}
          >
            <DownloadIcon style={{ fontSize: "20px" }} />
            <span className="font-medium">Export CSV</span>
          </button>
        </div>
      </div>

      {/* Quiz Details Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className="grid grid-cols-4 gap-6">
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Total Questions
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {record.question_count || "—"}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Pass Percentage
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {record.pass_percentage ? `${record.pass_percentage}%` : "—"}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Retake Limit
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {record.retake_limit === null || record.retake_limit === undefined
                ? "∞"
                : record.retake_limit}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Completions
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {record.total_submissions} / {record.assigned_count}
            </div>
          </div>
        </div>
      </div>

      {/* Submissions Table */}
      <div className="-mx-6 border-y border-slate-200 bg-white">
        {hasEmployeeData ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Team Member
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Result
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Attempts
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Date Completed
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {groupedSubmissions.map((group, groupIndex) => (
                  <React.Fragment key={`employee-${group.employee_id}`}>
                    {group.attempts.map((attempt, attemptIndex) => {
                      const isFirst = attemptIndex === 0;
                      const isLast = attemptIndex === group.attempts.length - 1;
                      const paddingClass = isFirst && isLast ? 'py-4' : isFirst ? 'pt-4 pb-3' : isLast ? 'pt-3 pb-4' : 'py-3';
                      // Legacy attempts have no detailed data to review.
                      const reviewable = !attempt.is_legacy && !!attempt.attempt_uuid;

                      return (
                      <tr
                        key={attempt.attempt_uuid ? `attempt-${attempt.attempt_uuid}` : `legacy-${group.employee_id}-${attemptIndex}`}
                        onClick={reviewable ? () => handleViewAttempt(attempt.attempt_uuid) : undefined}
                        className={`${attemptIndex === 0 ? 'border-t border-slate-200' : ''}${
                          reviewable ? ' cursor-pointer hover:bg-slate-50 transition-colors' : ''
                        }`}
                      >
                        {/* Employee Name - only show on first attempt. When it
                            row-spans multiple attempts, this cell lives in the
                            first attempt's <tr>, so a click anywhere in it would
                            open attempt #1 regardless of which attempt it's
                            visually next to. Suppress the row's review click here
                            for multi-attempt groups. */}
                        {attemptIndex === 0 ? (
                          <td
                            className={`px-6 py-4 whitespace-nowrap align-top${
                              group.attempts.length > 1 ? " cursor-default" : ""
                            }`}
                            rowSpan={group.attempts.length}
                            onClick={
                              group.attempts.length > 1
                                ? (e) => e.stopPropagation()
                                : undefined
                            }
                          >
                            <div className="flex items-center">
                              <div className="mr-3 shrink-0">
                                <PersonAvatar
                                  view="daily"
                                  name={group.employee_name}
                                  bgColor={
                                    employeeAvatarColorByUserId?.[
                                      group.employee_id
                                    ] || "bg-slate-600"
                                  }
                                />
                              </div>
                              <div>
                                <div className="text-xs font-medium text-slate-900">
                                  {group.employee_name}
                                </div>
                                {group.attempts.length > 1 && (
                                  <div className="text-xs text-slate-500 mt-1">
                                    {group.attempts.length} attempts
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        ) : null}

                        {/* Score */}
                        <td className={`px-6 whitespace-nowrap ${paddingClass}`}>
                          {attempt.is_legacy ? (
                            // Legacy completion - no detailed data available
                            <div>
                              <div className="text-sm text-slate-900">Passed</div>
                              <div className="text-xs text-slate-500">
                                Legacy completion
                              </div>
                            </div>
                          ) : attempt.score !== null && attempt.score !== undefined ? (
                            // Modern completion with detailed attempt data
                            // (the whole row opens the attempt review).
                            <div>
                              <div className="text-sm font-semibold text-slate-900">
                                {attempt.score}%
                              </div>
                              <div className="text-xs text-slate-500">
                                {attempt.correct_answers} / {record.question_count} correct
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-slate-400">—</div>
                          )}
                        </td>

                        {/* Result */}
                        <td className={`px-6 whitespace-nowrap ${paddingClass}`}>
                          <div className="flex items-center gap-2">
                            {attempt.passed ? (
                              <>
                                <CheckCircleIcon
                                  className="text-emerald-600"
                                  style={{ fontSize: "20px" }}
                                />
                                <span className="text-sm font-medium text-emerald-700">
                                  Passed
                                </span>
                              </>
                            ) : (
                              <>
                                <CancelIcon
                                  className="text-red-600"
                                  style={{ fontSize: "20px" }}
                                />
                                <span className="text-sm font-medium text-red-700">
                                  Failed
                                </span>
                              </>
                            )}
                          </div>
                        </td>

                        {/* Attempts */}
                        <td className={`px-6 whitespace-nowrap ${paddingClass}`}>
                          {attempt.is_legacy ? (
                            <div className="text-sm text-slate-500">N/A</div>
                          ) : attempt.attempt_number !== null && attempt.attempt_number !== undefined ? (
                            <div className="text-sm text-slate-900">
                              {attempt.attempt_number} /{" "}
                              {record.retake_limit === null ? "∞" : record.retake_limit}
                            </div>
                          ) : (
                            <div className="text-sm text-slate-400">—</div>
                          )}
                        </td>

                        {/* Date Completed */}
                        <td className={`px-6 whitespace-nowrap ${paddingClass}`}>
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <CalendarTodayIcon style={{ fontSize: "16px" }} />
                            <span>{formatDateReadable(attempt.date_completed)}</span>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
                {filteredIncompleteAssignments.map((assignment, index) => (
                  <tr
                    key={`pending-${index}`}
                    className="border-t border-slate-200 hover:bg-slate-50 transition-colors duration-150"
                  >
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="mr-3 shrink-0">
                          <PersonAvatar
                            view="daily"
                            name={assignment.employee_name}
                            bgColor={
                              employeeAvatarColorByUserId?.[
                                assignment.employee_id
                              ] || "bg-slate-600"
                            }
                          />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-slate-900">
                            {assignment.employee_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="text-sm text-slate-500">Not yet attempted</div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                          Pending
                        </span>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="text-sm text-slate-500">
                        0 / {record.retake_limit === null ? "∞" : record.retake_limit}
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="text-sm text-slate-500">—</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <InfoOutlinedIcon
              className="text-slate-300 mx-auto mb-4"
              style={{ fontSize: "48px" }}
            />
            <p className="text-slate-600 font-medium mb-2">
              Employee-level submission data not available
            </p>
            <p className="text-slate-400 text-sm">
              Summary: {record.total_submissions} of {record.assigned_count} employees
              have completed this quiz.
            </p>
          </div>
        )}
      </div>

      {/* Quiz Review Modal */}
      <QuizReviewModal
        open={isReviewModalOpen}
        handleClose={handleCloseReviewModal}
        attemptUuid={selectedAttemptUuid}
        homeCompany={homeCompany}
        language={language}
      />
    </div>
  );
}

export default QuizRecordTable;
